/**
 * E2EE encryption module — single shared implementation.
 *
 * All business logic lives here. Platform-specific crypto primitives
 * are resolved automatically via Metro bundler:
 *   - crypto.native.ts → iOS & Android (expo-crypto, @noble/*, SecureStore)
 *   - crypto.web.ts    → Web browser (Web Crypto API, IndexedDB)
 *
 * Algorithms: PBKDF2 (600k iterations, SHA-256) + AES-256-GCM
 * Recovery phrase: 24-word BIP39
 */

import { BIP39_WORDLIST } from "./bip39-wordlist";
import {
  arrayBufferToBase64,
  base64ToUint8Array,
  hexToUint8Array,
  uint8ArrayToHex,
} from "./encoding";
import {
  getRandomBytes,
  deriveKeyRaw,
  sha256,
  aesGcmEncrypt,
  aesGcmDecrypt,
  storeKeyToStorage,
  getKeyFromStorage,
  clearKeyFromStorage,
  cryptoKeyToRawBytes,
} from "./crypto";
import { deriveX25519KeyPair } from "./teamCrypto";
import { lazySyncTeamKey } from "./teamKeySync";
import { useEncryptionStore } from "../stores/encryptionStore";

const PBKDF2_ITERATIONS = 600_000;

// ── Recovery Phrase Generation ──────────────────────────────────────────────

/**
 * Generate a 24-word BIP39 recovery phrase using cryptographically secure randomness.
 */
export function generateRecoveryPhrase(): string[] {
  const randomBytes = getRandomBytes(48); // 2 bytes per word × 24 words
  const words: string[] = [];
  for (let i = 0; i < 24; i++) {
    const val = (randomBytes[i * 2] << 8) | randomBytes[i * 2 + 1];
    words.push(BIP39_WORDLIST[val % 2048]);
  }
  return words;
}

/**
 * Generate a random 16-byte salt, returned as a hex string.
 */
export function generateSalt(): string {
  return uint8ArrayToHex(getRandomBytes(16));
}

// ── Key Derivation ──────────────────────────────────────────────────────────

/**
 * Derive a 256-bit AES key from a recovery phrase and salt using PBKDF2.
 * Returns an opaque key (Uint8Array on native, CryptoKey on web).
 */
export async function deriveKey(
  phrase: string,
  salt: string
): Promise<unknown> {
  const encoder = new TextEncoder();
  const saltBytes = hexToUint8Array(salt);
  return deriveKeyRaw(encoder.encode(phrase), saltBytes, PBKDF2_ITERATIONS);
}

/**
 * Derive full key material from a recovery phrase: raw seed + X25519 key pair.
 * Used for the team-based key model where the PBKDF2 output becomes an X25519 identity key.
 */
export async function deriveKeyMaterial(
  phrase: string,
  salt: string,
): Promise<{
  rawSeed: Uint8Array;
  x25519PrivateKey: Uint8Array;
  x25519PublicKey: Uint8Array;
}> {
  const pbkdf2Output = await deriveKey(phrase, salt);
  const rawSeed = await cryptoKeyToRawBytes(pbkdf2Output);
  const { privateKey, publicKey } = deriveX25519KeyPair(rawSeed);
  return { rawSeed, x25519PrivateKey: privateKey, x25519PublicKey: publicKey };
}

// ── Hashing ─────────────────────────────────────────────────────────────────

/**
 * SHA-256 hash of the recovery phrase for server-side storage/validation.
 * Returns a hex-encoded hash string.
 */
export async function hashRecoveryPhrase(phrase: string): Promise<string> {
  const encoder = new TextEncoder();
  const hash = await sha256(encoder.encode(phrase));
  return uint8ArrayToHex(hash);
}

// ── Encryption / Decryption ─────────────────────────────────────────────────

/**
 * Encrypt plaintext with AES-256-GCM.
 * If `key` is omitted, the stored key is used.
 */
export async function encrypt(
  plaintext: string,
  key?: unknown
): Promise<{ encrypted: string; metadata: { iv: string } }> {
  const resolvedKey = key ?? (await getStoredKeyOrThrow());
  const encoder = new TextEncoder();
  const iv = getRandomBytes(12);

  const ciphertext = await aesGcmEncrypt(resolvedKey, iv, encoder.encode(plaintext));

  return {
    encrypted: arrayBufferToBase64(ciphertext),
    metadata: {
      iv: arrayBufferToBase64(iv),
    },
  };
}

/**
 * Decrypt ciphertext with AES-256-GCM.
 * If `key` is omitted, the stored key is used.
 */
export async function decrypt(
  encrypted: string,
  metadata: { iv: string },
  key?: unknown
): Promise<string> {
  if (!encrypted) return "";
  const resolvedKey = key ?? (await getStoredKeyOrThrow());
  const iv = base64ToUint8Array(metadata.iv);
  const ciphertext = base64ToUint8Array(encrypted);

  const decrypted = await aesGcmDecrypt(resolvedKey, iv, ciphertext);

  return new TextDecoder().decode(decrypted);
}

/**
 * Encrypt a title string. Returns the base64 ciphertext and its IV.
 */
export async function encryptTitle(
  plaintext: string,
  key?: unknown
): Promise<{ title_encrypted: string; title_iv: string }> {
  const { encrypted, metadata } = await encrypt(plaintext, key);
  return { title_encrypted: encrypted, title_iv: metadata.iv };
}

/**
 * Decrypt a title given its base64 ciphertext and IV.
 */
export async function decryptTitle(
  encrypted: string,
  titleIv: string,
  key?: unknown
): Promise<string> {
  if (!encrypted || !titleIv) return "";
  return decrypt(encrypted, { iv: titleIv }, key);
}

/**
 * Build an encryption_metadata object from description IV and title IV.
 */
export function mergeEncryptionMetadata(
  descIv?: string,
  titleIv?: string,
  additionalIvs?: Record<string, string>
): Record<string, string> {
  const meta: Record<string, string> = {};
  if (descIv) meta.iv = descIv;
  if (titleIv) meta.title_iv = titleIv;
  if (additionalIvs) Object.assign(meta, additionalIvs);
  return meta;
}

/**
 * Decrypt a title from an entity that has title_encrypted + encryption_metadata.
 * Returns "Untitled" if decryption fails or no IV is present.
 *
 * If the entity has a `team_id`, the correct team key is resolved automatically.
 * Pass an explicit `key` to override dispatch (e.g. when you already have the key).
 */
export async function decryptEntityTitle(
  entity: { title_encrypted: string; encryption_metadata?: { title_iv?: string } | null; team_id?: string | null },
  key?: unknown,
): Promise<string> {
  if (entity.encryption_metadata?.title_iv) {
    try {
      const resolvedKey = key ?? await getKeyForTeam(entity.team_id ?? null);
      return await decryptTitle(entity.title_encrypted, entity.encryption_metadata.title_iv, resolvedKey);
    } catch {}
  }
  return "Untitled";
}

async function getStoredKeyOrThrow(): Promise<unknown> {
  const userId = useEncryptionStore.getState().currentUserId;
  // No fallback to an unscoped slot. `getStoredKey(undefined)` used to read a
  // legacy global record here — not "no key", a DIFFERENT record, one that no
  // writer has ever used — so a null `currentUserId` silently looked in the
  // wrong place instead of saying it was not initialised.
  if (!userId) {
    throw new Error("Encryption is not initialised for a signed-in user yet.");
  }
  const key = await getStoredKey(userId);
  if (!key) {
    throw new Error(
      "No encryption key found. Please set up encryption first."
    );
  }
  return key;
}

/**
 * Resolve the correct AES key for a given team.
 *
 * - Personal team (or null): the in-memory copy `syncTeamKeys` unwrapped on
 *   this login, falling back to the persisted device key — which is the SAME
 *   key, written by `persistPersonalTeamKey`, and is what makes offline work.
 * - Shared team: returns the in-memory team key from encryptionStore.
 *   If missing, attempts a lazy sync (re-derives X25519 from persisted seed).
 */
export async function getKeyForTeam(teamId: string | null): Promise<unknown> {
  const encStore = useEncryptionStore.getState();
  const personalTeamId = encStore.personalTeamId;

  if (!teamId || teamId === personalTeamId) {
    // Memory first. The sync fetched this from the server moments ago, so it is
    // authoritative; the persisted copy is for when there was no sync. Checking
    // disk first turned a device with a stale or missing persisted key — the
    // restore and transfer defects — into an app where nothing rendered, while
    // the right key sat in memory unconsulted.
    if (personalTeamId) {
      const inMemory = encStore.getTeamKey(personalTeamId);
      if (inMemory) return inMemory;
    }
    return getStoredKeyOrThrow();
  }

  // Shared team → look up in-memory team key
  const teamKey = encStore.getTeamKey(teamId);
  if (teamKey) return teamKey;

  // Lazy sync: re-derive X25519 from persisted seed, re-fetch wrapped key
  const synced = await lazySyncTeamKey(teamId);
  if (synced) return synced;

  throw new Error(`Team key not found for team ${teamId}. Try syncing keys.`);
}

// ── Key Storage ─────────────────────────────────────────────────────────────

/**
 * Store the device key — the PERSONAL TEAM KEY — in platform secure storage.
 * See docs/encryption.md, "User-Scoped Key Naming", for why nothing else may
 * be written here.
 */
export async function storeKey(key: unknown, userId: string): Promise<void> {
  return storeKeyToStorage(key, userId);
}

/**
 * Retrieve the stored device key. Returns null only when no key is stored;
 * a storage failure throws (see ./cryptoErrors).
 */
export async function getStoredKey(userId: string): Promise<unknown | null> {
  return getKeyFromStorage(userId);
}

/**
 * Clear the stored device key.
 */
export async function clearStoredKey(userId: string): Promise<void> {
  return clearKeyFromStorage(userId);
}
